import React from 'react';
import useThemeStore from "../../store/themeStore";
import { ThemeProps } from "../../utils/theme";
import ReactDOM from 'react-dom';

interface ServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  content: {
    title: string;
    description: string;
    productId: string;
  };
  onEnquire: (productId: string) => void;
}

const ServiceModal: React.FC<ServiceModalProps> = ({ isOpen, onClose, content, onEnquire }) => {
  const themes = useThemeStore((state) => state.theme as unknown as ThemeProps);

  // Lock body scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Utility function to check for white or near-white shades
  const isWhiteOrShade = (color: string) => {
    if (color.toLowerCase() === "white" || color === "rgba(255,255,255,1)") return true;
    if (/^#fff[a-f0-9]{0,3}$/i.test(color)) return true;

    const rgba = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
    if (rgba) {
      const [, r, g, b] = rgba.map(Number);
      return r >= 240 && g >= 240 && b >= 240;
    }

    return false;
  };

  const handleHover = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, hoverColor: string) => {
    const hoverBg = hoverColor || themes.color1;
    const originalBg = themes.color2;
    
    if (e.type === 'mouseover') {
      e.currentTarget.style.background = hoverBg;
      e.currentTarget.style.color = isWhiteOrShade(hoverBg) ? "#000000" : "#ffffff";
    } else {
      e.currentTarget.style.background = originalBg;
      e.currentTarget.style.color = isWhiteOrShade(originalBg) ? "#000000" : "#ffffff";
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div 
      className="fixed inset-0 flex items-center justify-center z-[1001]"
      style={{ 
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        transition: 'all 0.3s ease',
        opacity: isOpen ? 1 : 0,
        visibility: isOpen ? 'visible' : 'hidden'
      }}
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg p-8 max-w-md w-full shadow-lg transform transition-transform duration-300" 
        style={{
          backgroundColor: "white",
          transform: isOpen ? 'scale(1)' : 'scale(0.95)',
          opacity: isOpen ? 1 : 0,
          transition: 'transform 0.3s ease, opacity 0.3s ease'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-xl font-bold">{content.title}</h3>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold transition-transform duration-200 hover:scale-125"
            style={{
              transition: 'transform 0.2s',
              display: 'inline-block',
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.3)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            &times;
          </button>
        </div>
        
        <div className="mb-6">
          <p dangerouslySetInnerHTML={{ __html: content.description }}></p>
        </div>
        
        <div className="flex justify-between">
          <button 
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 transition duration-200"
          >
            Close
          </button>
          
          {content.productId && (
            <button 
              onClick={() => onEnquire(content.productId)}
              className="px-4 py-2 rounded-md transition duration-300 hover:shadow-md"
              style={{
                background: themes.color2,
                color: isWhiteOrShade(themes.color2) ? "#000000" : "#ffffff",
              }}
              onMouseOver={(e) => handleHover(e, themes.color1)}
              onMouseOut={(e) => handleHover(e, themes.color2)}
            >
              Enquire Now
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ServiceModal; 